import React from 'react';
import styled from '@emotion/styled';
import { APP_CONTAINER_WIDTH } from '@styles/constants/spacings';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Image from '@base/Image';
import Typography from '@unicons-base/Typography/Typography';
import AnyfeedButton from '@unicons-comp/AnyfeedButton/AnyfeedButton';
import useWindowSize from '@hooks/useWindowSize';
import { emptyObject } from '@constants/empty';
import { useRouter } from 'next/router';
import { getImage } from '@utils/imageHelper';

interface AnyfeedIconCardProps {
  heading: any;
  titleVariant: any;
  cta: any;
  image: any;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  max-width: ${APP_CONTAINER_WIDTH};
  justify-content: space-between;
  align-item: flex-start;
  flex-direction: row;
  margin: 0 auto;
  background: var(--color-i-icon-dark-primary);
  background-image: url(${getImage('icon-card-banner.png')});
  background-size: contain;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoTablet} {
    margin: 0 ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    padding: 0px ${Spacings.SPACING_4B};
    max-width: 100%;
    background-size: cover;
    margin: 0 auto;
    justify-content: center;
    align-items: center;
    flex-direction: row-reverse;
    margin-bottom: 0px;
    margin-left: 16px;
    margin-right: 16px;
  }
`;

const StyledHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  ${mediaQuery.uptoTablet} {
    padding-left: ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding-left: ${Spacings.SPACING_5B};
  }
`;

const StyledTitle = styled(Typography)`
  max-width: 510px;
  background: var(--color-i-icon-light-tertiary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  &::selection {
    -webkit-text-fill-color: var(--color-i-all-black);
  }
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H2};
    max-width: 570px;
    text-align: center;
    line-height: 38px;
  }
`;

const StyledTitleCover = styled.div`
  position: relative;
  width: 182px;
  height: 80.5px;
  margin-left: auto;
  margin-right: auto;
  ${mediaQuery.uptoMobile} {
    width: 120px;
    height: 54px;
  }
`;

const StyledButton = styled(AnyfeedButton)`
  margin-top: ${Spacings.SPACING_10B};
  width: 228px;
  height: 40px;
  ${mediaQuery.uptoMobile} {
    width: 328px;
    margin: auto;
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const StyledCoverWrapper = styled.div<{
  $isIconCard: boolean;
}>`
  position: absolute;
  border-radius: 8px;
  overflow: hidden;
  height: ${({ $isIconCard }) => $isIconCard && '250px'};
  width: ${({ $isIconCard }) => $isIconCard && '200px'};
  bottom: ${({ $isIconCard }) => $isIconCard && '0px'};
  top: ${({ $isIconCard }) => ($isIconCard ? 'unset' : '0')};
  right: ${({ $isIconCard }) => ($isIconCard ? '55px' : '0')};
  max-width: 504px;
  ${mediaQuery.uptoMobile} {
    position: relative;
    width: 148px;
    height: 220px;
    min-width: 148px;
    right: 0;
    & img {
      object-fit: cover;
    }
  }
`;

const AnyfeedIconCard: React.FC<AnyfeedIconCardProps> = ({
  heading,
  titleVariant,
  cta,
  image
}) => {
  const { isMobile } = useWindowSize();
  const router = useRouter();
  const { slug: courseSlug } = cta?.metadata || emptyObject;

  const handleIconClick = () => {
    router.push(`/icons/courses/${courseSlug}`);
  };

  return (
    <Wrapper onClick={() => handleIconClick()}>
      <StyledHeaderContent>
        {heading?.titleImage && (
          <StyledTitleCover>
            <Image
              src={heading?.titleImage}
              alt="Unacademy Icons"
              layout="fill"
            />
          </StyledTitleCover>
        )}
        {heading?.title && (
          <StyledTitle
            variant={titleVariant}
            color={heading?.titleColor || 'var(--color-i-icon-light-tertiary)'}
          >
            {heading.title}
          </StyledTitle>
        )}

        {cta && !isMobile && (
          <StyledButton
            title={cta?.title}
            titleColor={cta?.titleColor}
            backgroundColor={cta?.backgroundColor}
            isSubscriptionCta={cta?.category === 'subscription'}
            isCourseCta={cta?.category === 'course'}
            ctaMetaData={cta?.metadata}
          />
        )}
      </StyledHeaderContent>
      {image?.image && (
        <StyledCoverWrapper $isIconCard>
          <Image
            src={image?.image}
            alt={heading?.title || 'Unacademy Icons'}
            layout="fill"
          />
        </StyledCoverWrapper>
      )}
    </Wrapper>
  );
};

AnyfeedIconCard.displayName = 'AnyfeedIconCard';

export default AnyfeedIconCard;
